/**
 * O leitor: a transcrição aberta para ler, não para baixar.
 *
 * <p>Um {@code <pre>} com o arquivo cru é o que o acervo faz com um .txt, e serve para
 * um log. Uma transcrição de reunião é <b>diálogo</b>, e ler diálogo num bloco
 * monoespaçado de 3.000 linhas é o que faz ninguém ler. Aqui as falas viram falas: quem
 * disse fica destacado, o carimbo de tempo sai da frente, e a linha tem medida de leitura.
 *
 * <p>A busca existe pelo mesmo motivo. A pergunta que se faz a uma transcrição quase
 * nunca é "o que foi dito"; é "onde foi que falamos de X". Sem ela, a resposta é rolar.
 *
 * <p>PDF vai para o visualizador do próprio navegador — ele já sabe rolar, buscar e
 * imprimir, e nada do que está acima se aplica a um arquivo que não é texto.
 *
 * <p>HTML — o diário de sessão que o time gera da reunião — vai para o
 * {@code QuadroHtmlIsolado}: o servidor o entrega como texto plano, e é o iframe de
 * origem opaca que o devolve à vida sem lhe dar a sessão de quem abriu. Falas, busca
 * e oradores não se aplicam: uma página desenha o próprio conteúdo.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { QuadroHtmlIsolado } from '../../comum/QuadroHtmlIsolado';
import { separarEmFalas } from './falas';
import * as api from './transcricoesApi';
import type { Transcricao } from './transcricoesApi';
import { dataPorExtenso, tamanhoLegivel } from './transcricoesApi';

/** Uma cor estável por orador, tirada do nome — para o olho separar quem fala. */
function corDe(nome: string): string {
  const CORES = ['#C9A25A', '#5B8FB9', '#4E9C6B', '#A87BD0', '#C0703C', '#3FA0A0', '#A9C46C'];
  let soma = 0;
  for (let i = 0; i < nome.length; i++) soma = (soma * 31 + nome.charCodeAt(i)) % 100000;
  return CORES[soma % CORES.length];
}

/** Quebra o texto nos trechos que casam com a busca, para o destaque ser só neles. */
function realcar(texto: string, busca: string) {
  const alvo = busca.trim();
  if (!alvo) return texto;
  const partes = texto.split(new RegExp(`(${alvo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return partes.map((p, i) =>
    p.toLowerCase() === alvo.toLowerCase() ? (
      <mark key={i} className="tr-marca">
        {p}
      </mark>
    ) : (
      p
    )
  );
}

export function LeitorDeTranscricao({
  transcricao,
  aoFechar,
  aoExcluir,
}: {
  transcricao: Transcricao;
  aoFechar: () => void;
  aoExcluir: () => void;
}) {
  const ehPdf = transcricao.tipo === 'PDF';
  const ehHtml = transcricao.tipo === 'HTML';
  // Só o texto cru vira diálogo; PDF e HTML desenham o próprio conteúdo.
  const ehFalas = !ehPdf && !ehHtml;
  const [busca, setBusca] = useState('');

  // O conteúdo de um id não muda — trocar o arquivo é subir outra transcrição.
  const pdf = useQuery({
    queryKey: ['transcricao', transcricao.id, 'blob'],
    queryFn: () => api.conteudoBlob(transcricao.id),
    enabled: ehPdf,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const texto = useQuery({
    queryKey: ['transcricao', transcricao.id, 'texto'],
    queryFn: () => api.conteudoTexto(transcricao.id),
    enabled: !ehPdf,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  /**
   * O object URL nasce aqui e morre aqui. Guardá-lo no cache faria a segunda abertura
   * receber um endereço já revogado.
   */
  const [urlDoPdf, setUrlDoPdf] = useState<string | null>(null);
  useEffect(() => {
    if (!pdf.data) return;
    const url = URL.createObjectURL(pdf.data);
    setUrlDoPdf(url);
    return () => {
      URL.revokeObjectURL(url);
      setUrlDoPdf(null);
    };
  }, [pdf.data]);

  const falas = useMemo(
    () => (ehFalas && texto.data ? separarEmFalas(texto.data) : []),
    [ehFalas, texto.data]
  );

  const visiveis = useMemo(() => {
    const alvo = busca.trim().toLowerCase();
    if (!alvo) return falas;
    return falas.filter(
      (f) => f.texto.toLowerCase().includes(alvo) || (f.quem ?? '').toLowerCase().includes(alvo)
    );
  }, [falas, busca]);

  const oradores = useMemo(
    () => [...new Set(falas.map((f) => f.quem).filter((q): q is string => !!q))],
    [falas]
  );

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  const carregando = ehPdf ? pdf.isLoading : texto.isLoading;
  const erro = ehPdf ? pdf.error : texto.error;

  async function baixar() {
    const blob = ehPdf ? pdf.data : new Blob([texto.data ?? '']);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = transcricao.nomeOriginal ?? `${transcricao.titulo}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="tr-leitor" onClick={aoFechar}>
      <div className="tr-leitor-cx" onClick={(e) => e.stopPropagation()}>
        <header>
          <span className={`tr-selo${ehPdf ? ' pdf' : ehHtml ? ' html' : ''}`}>
            {ehPdf ? 'PDF' : ehHtml ? 'HTML' : 'TXT'}
          </span>
          <div className="tr-leitor-tt">
            <b>{transcricao.titulo}</b>
            <span>
              {dataPorExtenso(transcricao.reuniaoEm)} · {tamanhoLegivel(transcricao.bytes)}
              {transcricao.enviadoPor && ` · enviado por ${transcricao.enviadoPor}`}
            </span>
          </div>

          {ehFalas && (
            <input
              className="tr-busca"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar no que foi dito…"
            />
          )}
          <button type="button" onClick={() => void baixar()} title="Baixar o original">
            ⤓ Baixar
          </button>
          <button type="button" className="perigo" onClick={aoExcluir} title="Excluir">
            Excluir
          </button>
          <button type="button" onClick={aoFechar} title="Fechar (Esc)">
            ✕
          </button>
        </header>

        {ehFalas && oradores.length > 0 && (
          <div className="tr-oradores">
            {oradores.map((quem) => (
              <span key={quem} className="tr-orador" style={{ '--c': corDe(quem) } as React.CSSProperties}>
                {quem}
              </span>
            ))}
            {busca.trim() && (
              <span className="tr-achados">
                {visiveis.length} fala{visiveis.length === 1 ? '' : 's'} com “{busca.trim()}”
              </span>
            )}
          </div>
        )}

        <div className="tr-leitor-corpo">
          {carregando && <div className="tr-espera">Abrindo…</div>}
          {erro && (
            <div className="tr-espera erro">
              {erro instanceof Error ? erro.message : 'Não foi possível abrir.'}
            </div>
          )}

          {!carregando && !erro && ehPdf && urlDoPdf && (
            <iframe className="tr-quadro" src={urlDoPdf} title={transcricao.titulo} />
          )}

          {!carregando && !erro && ehHtml && texto.data != null && (
            <QuadroHtmlIsolado
              className="tr-quadro"
              titulo={transcricao.titulo}
              html={texto.data}
            />
          )}

          {!carregando && !erro && ehFalas && (
            <article className="tr-falas">
              {visiveis.length === 0 && (
                <div className="tr-espera">
                  {busca.trim() ? 'Ninguém falou nisso.' : 'A transcrição está vazia.'}
                </div>
              )}
              {visiveis.map((f, i) => (
                <p key={i} className={f.quem ? 'tr-fala' : 'tr-fala solta'}>
                  {f.quem && (
                    <span className="tr-quem" style={{ '--c': corDe(f.quem) } as React.CSSProperties}>
                      {f.quem}
                      {f.quando && <i>{f.quando}</i>}
                    </span>
                  )}
                  <span className="tr-dito">{realcar(f.texto, busca)}</span>
                </p>
              ))}
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
